
import React, { useRef, useState } from 'react';
import { useApp } from '../store';
import { 
  Rocket, 
  Globe, 
  Database, 
  CheckCircle2, 
  Download, 
  Upload, 
  ShieldCheck, 
  Github,
  Lock
} from 'lucide-react';

const DeploymentGuide = () => {
  const { exportSystemData, importSystemData, projects } = useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        importSystemData(content);
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-24">
      {/* Visual Header */}
      <div className="text-center space-y-6">
        <div className="inline-flex items-center justify-center w-24 h-24 bg-indigo-600 rounded-[2.5rem] text-white shadow-2xl shadow-indigo-200 border-4 border-white transform hover:scale-110 transition-transform cursor-pointer">
          <Rocket className="w-12 h-12" />
        </div>
        <div className="space-y-2">
          <h2 className="text-5xl font-black text-slate-900 tracking-tighter uppercase italic">Go Live Guide</h2>
          <p className="text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px] bg-white border border-slate-200 px-6 py-2 rounded-full inline-block shadow-sm">
            Agency Owner Launch Protocol
          </p>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Step 1: GitHub */}
        <div className="bg-white p-8 rounded-[3rem] border border-slate-200 shadow-xl shadow-slate-100/50 flex flex-col md:flex-row gap-8 items-center group hover:border-indigo-300 transition-all">
          <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center text-white shrink-0 group-hover:rotate-6 transition-transform">
            <Github className="w-8 h-8" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl font-black text-slate-900 uppercase">Step 1: Save Code to GitHub</h3>
            <p className="text-slate-500 font-medium text-sm mt-1">This stores your app securely in the cloud for free.</p>
          </div>
          <a href="https://github.com/new" target="_blank" className="px-6 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-black uppercase tracking-widest text-slate-600 hover:bg-slate-900 hover:text-white transition-all">
            Create Repo
          </a>
        </div>

        {/* Step 2: Vercel */}
        <div className="bg-white p-8 rounded-[3rem] border border-slate-200 shadow-xl shadow-slate-100/50 flex flex-col md:flex-row gap-8 items-center group hover:border-indigo-300 transition-all">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shrink-0 group-hover:rotate-6 transition-transform">
            <Globe className="w-8 h-8" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl font-black text-slate-900 uppercase">Step 2: Launch on Vercel</h3>
            <p className="text-slate-500 font-medium text-sm mt-1">This gives you a public link for your team to use.</p>
          </div>
          <a href="https://vercel.com/new" target="_blank" className="px-6 py-3 bg-indigo-50 border border-indigo-200 rounded-xl text-xs font-black uppercase tracking-widest text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all">
            Deploy Now
          </a>
        </div>

        {/* Step 3: Migration (The important one for data) */}
        <div className="bg-slate-900 p-10 rounded-[3.5rem] text-white relative overflow-hidden shadow-2xl shadow-indigo-900/40">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2"></div>
          
          <div className="relative z-10 space-y-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <Database className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-black uppercase italic tracking-tight">Step 3: Move your Data</h3>
            </div>
            
            <p className="text-slate-400 font-medium text-lg leading-relaxed max-w-xl">
              Once your app is live on its new link, follow these two buttons to transfer your current projects to the live version.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-6 bg-white/5 border border-white/10 rounded-3xl space-y-4">
                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Part A: Local Export</p>
                <p className="text-xs text-slate-400">Download your current {projects.length} projects to your computer.</p>
                <button 
                  onClick={exportSystemData}
                  className="w-full py-4 bg-indigo-600 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-indigo-700 transition-all"
                >
                  <Download className="w-4 h-4 inline mr-2" /> Download Data
                </button>
              </div>

              <div className="p-6 bg-white/5 border border-white/10 rounded-3xl space-y-4">
                <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Part B: Live Import</p>
                <p className="text-xs text-slate-400">Open your live link and upload the file here.</p>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-4 bg-white text-slate-900 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-100 transition-all"
                >
                  <Upload className="w-4 h-4 inline mr-2" /> Upload to Live
                </button>
                <input type="file" ref={fileInputRef} onChange={handleImport} className="hidden" accept=".json" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security Note */}
      <div className="bg-amber-50 border border-amber-200 p-8 rounded-[2.5rem] flex items-start gap-6">
        <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600 shrink-0">
          <Lock className="w-6 h-6" />
        </div>
        <div className="space-y-2">
          <h4 className="text-sm font-black text-amber-900 uppercase tracking-widest">Team Credentials</h4>
          <p className="text-sm text-amber-700 font-medium leading-relaxed">
            Your team can log in using their **User Name** and the secure password established by the Administrator in the Team Management section.
          </p>
        </div>
      </div>

      <div className="text-center pt-8">
         <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.4em] flex items-center justify-center gap-3">
           <ShieldCheck className="w-4 h-4 text-emerald-500" />
           Nice Digital Enterprise &bull; Ready for Global Access
         </p>
      </div>
    </div>
  );
};

export default DeploymentGuide;
